import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { MISSION_STATE_LABELS } from "./RequestDetail";
import { Activity, ArrowRight, Calendar, Loader2, MapPin } from "lucide-react";

// Statuts projetés considérés comme « en cours » (entre acceptation et clôture)
const ACTIVE_STATUSES = ["accepted", "in_progress"];

// Qui a la main, selon l'état mission et le rôle du lecteur
function actionFor(state, isPro) {
  const A = (text) => ({ text, mine: true });   // action attendue du lecteur
  const W = (text) => ({ text, mine: false });  // en attente de l'autre partie
  switch (state) {
    case "CREATED":
      return isPro ? W("En attente du paiement de l'acompte par le client")
                   : A("Payez l'acompte pour lancer la mission");
    case "VALIDATED":
    case "AWAITING_BALANCE":
      return isPro ? W("Séquestre en cours de constitution (solde à J-3)")
                   : W("Acompte encaissé — solde débité automatiquement à J-3");
    case "BALANCE_CAPTURED":
    case "AWAITING_PICKUP":
      return isPro ? A("À vous : photos au chargement pour confirmer la prise en charge")
                   : W("Séquestre complet — en attente de la prise en charge");
    case "LOADED":
      return isPro ? A("À vous : photos à la livraison pour clôturer le transport")
                   : W("Véhicule chargé — transport en cours");
    case "DELIVERY_PENDING_CONFIRMATION":
      return isPro ? W("Livraison documentée — validation du client sous 72 h")
                   : A("À vous : vérifiez les photos et confirmez la livraison");
    case "DELIVERED":
      return isPro ? W("Livraison validée — reversement des fonds en cours")
                   : W("Livraison validée — clôture en cours");
    case "DISPUTED":
    case "DISPUTE_ESCALATED":
      return W("Litige en cours — fonds gelés, médiation sous 7 jours ouvrés");
    default:
      return W("Mission en cours");
  }
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function EnCours() {
  const { user } = useAuth();
  const isPro = user?.role === "pro";
  const [items, setItems] = useState(null); // null = chargement

  const load = useCallback(async () => {
    try {
      const url = isPro ? "/pro/missions" : "/requests?scope=mine";
      const { data } = await api.get(url);
      const active = (data || []).filter((d) => ACTIVE_STATUSES.includes(d.status));
      const enriched = await Promise.all(active.map(async (d) => {
        try {
          const { data: mi } = await api.get(`/requests/${d.request_id}/mission`);
          return { ...d, mission: mi.mission, payment: mi.payment };
        } catch {
          return { ...d, mission: null, payment: null };
        }
      }));
      // Les missions où le lecteur a la main d'abord
      enriched.sort((a, b) => {
        const am = actionFor(a.mission?.state, isPro).mine ? 0 : 1;
        const bm = actionFor(b.mission?.state, isPro).mine ? 0 : 1;
        return am - bm;
      });
      setItems(enriched);
    } catch {
      setItems([]);
    }
  }, [isPro]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="flex pt-16">
        <Sidebar />
        <main className="flex-1 p-6 lg:p-8 max-w-5xl">
          <div className="mb-6">
            <div className="text-[10px] uppercase tracking-widest text-orange-600 font-bold">
              {isPro ? "Espace dépanneur" : "Espace client"}
            </div>
            <h1 className="font-display font-extrabold text-2xl text-slate-900 flex items-center gap-2">
              <Activity className="w-6 h-6 text-orange-600" /> Courses en cours
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {isPro
                ? "Vos missions actives, avec l'étape attendue de votre côté."
                : "Vos transports actifs, de l'acompte à la livraison."}
            </p>
          </div>

          {items === null ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-16 justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-orange-600" /> Chargement…
            </div>
          ) : items.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
              <Activity className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="font-display font-bold text-lg text-slate-900">Aucune course en cours</div>
              <p className="text-sm text-slate-500 mt-1 mb-5">
                {isPro
                  ? "Vos prochaines missions apparaîtront ici dès qu'une de vos offres sera acceptée."
                  : "Vos transports apparaîtront ici dès qu'une offre sera acceptée."}
              </p>
              <Link
                to={isPro ? "/dashboard" : "/requests/new"}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-display font-bold text-sm transition-colors"
              >
                {isPro ? "Voir le marketplace" : "Publier une demande"} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            <div className="space-y-3" data-testid="en-cours-list">
              {items.map((d) => {
                const state = d.mission?.state;
                const [chipLabel, chipCls] = MISSION_STATE_LABELS[state] || ["En cours", "bg-slate-100 text-slate-700"];
                const action = actionFor(state, isPro);
                const totalCents = d.payment?.amount_total_cents;
                return (
                  <Link
                    key={d.request_id}
                    to={`/requests/${d.request_id}`}
                    className={`block bg-white border rounded-xl p-4 transition-all hover:shadow-md ${
                      action.mine ? "border-orange-400" : "border-slate-200"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="font-display font-bold text-slate-900 truncate">{d.title}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-3 mt-1 flex-wrap">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3.5 h-3.5" /> {d.pickup?.city} → {d.dropoff?.city}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" /> {fmtDate(d.pickup_date)}
                          </span>
                          {totalCents ? (
                            <span className="font-semibold text-slate-700">{(totalCents / 100).toFixed(2)} €</span>
                          ) : null}
                        </div>
                      </div>
                      <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full flex-shrink-0 ${chipCls}`}>
                        {chipLabel}
                      </span>
                    </div>
                    <div className={`mt-3 text-xs flex items-center gap-2 rounded-lg px-3 py-2 ${
                      action.mine
                        ? "bg-orange-50 text-orange-800 border border-orange-200 font-semibold"
                        : "bg-slate-50 text-slate-500"
                    }`}>
                      {action.mine && <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" />}
                      {action.text}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}